import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { SessionDetailType, SessionType } from '../../types'
import { useAxiosInstance } from '../../api/AxiosInstanceContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { ReviewCasesContext } from './ReviewCasesContext';


interface ReviewSessionsContext {
    reviewSessions: SessionType[];
    reviewSessionsError: string | null;
    loadingReviewSessions: boolean;
    refreshReviewSessions: () => void;
    sessionDetails: Record<number, SessionDetailType>;
    getSessionDetail: (sessionId: number) => Promise<SessionDetailType>;
}

export const ReviewSessionsContext = createContext<ReviewSessionsContext>(undefined as any)

interface Props {
    children: React.ReactNode
}

export const ReviewSessionsProvider: React.FC<Props> = ({ children }) => {
    const axiosInstance = useAxiosInstance();
    const [reviewSessions, setReviewSessions] = useState([] as SessionType[]);
    const [reviewSessionsError, setReviewSessionsError] = useState<string | null>(null);
    const [loadingReviewSessions, setLoadingReviewSessions] = useState(true);
    const [sessionDetails, setSessionDetails] = useState<Record<number, SessionDetailType>>({});
    const { reviewCases } = useContext(ReviewCasesContext);

    const scope = useMemo(() => ({
        details: {} as Record<number, SessionDetailType>,
        pending: new Map<number, Promise<SessionDetailType>>(),
        generation: 0,
        active: true,
    }), [axiosInstance]);

    const caseIds = useMemo(() => reviewCases.map((reviewCase) => reviewCase.id), [reviewCases]);

    const fetchSessions = useCallback(() => {
        const generation = ++scope.generation;
        scope.details = {};
        scope.pending.clear();
        setSessionDetails({});
        setLoadingReviewSessions(true);
        setReviewSessionsError(null);
        axiosInstance.post('/review_sessions', JSON.stringify({ cases_ids: caseIds }))
            .then(({ data }) => {
                if (!scope.active || generation !== scope.generation) return;
                setReviewSessions(data);
                setReviewSessionsError(null);
            })
            .catch((e) => {
                if (scope.active && generation === scope.generation) {
                    setReviewSessionsError(getApiErrorMessage(e, 'Sessions could not be loaded.'));
                }
            })
            .finally(() => {
                if (scope.active && generation === scope.generation) setLoadingReviewSessions(false);
            });
    }, [axiosInstance, caseIds, scope]);

    useEffect(() => {
        scope.active = true;
        fetchSessions();
        return () => { scope.active = false; scope.generation++; };
    }, [fetchSessions, scope]);

    const getSessionDetail = useCallback((sessionId: number): Promise<SessionDetailType> => {
        const cached = scope.details[sessionId];
        if (cached) {
            return Promise.resolve(cached);
        }

        const pending = scope.pending.get(sessionId);
        if (pending) {
            return pending;
        }

        const generation = scope.generation;
        const request = axiosInstance
            .get<SessionDetailType>(`/review_session/?session_id=${sessionId}`)
            .then(({ data }) => {
                if (!scope.active || generation !== scope.generation) throw new Error('Session request superseded');
                if (!data || data.id !== sessionId) throw new Error('Unexpected session response');
                scope.details = { ...scope.details, [sessionId]: data };
                setSessionDetails(scope.details);
                return data;
            })
            .finally(() => {
                if (scope.pending.get(sessionId) === request) scope.pending.delete(sessionId);
            });

        scope.pending.set(sessionId, request);
        return request;
    }, [axiosInstance, scope]);

    const values = {
        reviewSessions,
        reviewSessionsError,
        loadingReviewSessions,
        refreshReviewSessions: fetchSessions,
        sessionDetails,
        getSessionDetail,
    }

    return <ReviewSessionsContext.Provider value={values}>{children}</ReviewSessionsContext.Provider>
}
